import { useRef, useState } from 'react';

import {
  defaultDropAnimationSideEffects,
  DndContext,
  DropAnimation,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { format } from 'date-fns';
import { PlanAppointmentsRequest } from 'generated';
import { useRecoilState } from 'recoil';

import { Event } from '../types';
import { EditAppointmentType } from 'RestAPIs/activities/types';

import {
  APNotification,
  findItem,
  appendCorrelationToken,
  getConflictingEvents,
  notificationsToHashTable,
  planningEntitiesToEvents,
  handlePlanAppointments as handlePlanAppointmentsUtil,
  unassignAppointment as unassignAppointmentUtil
} from '../utils';

import { ConnectAppointmentGroupingPayload } from 'RestAPIs/activities';
import { mutations } from 'RestAPIs/rapini';

import {
  dialogManagerActiveAtom,
  dragActionActiveAtom,
  planResponsePlanningEntityIdentifiersAtom,
  shouldShowPopoverDialogAtom
} from '../atoms';

import { Box } from '@mui/material';

import { LoadingOverlay } from 'components/shared';

import { DialogType } from '../components/DialogManager/DialogManager';

import { UsePlanningReturn } from '../hooks/usePlanning';
import RouteOverview from './RouteOverview/RouteOverview';
import { PopOverConfirmationContext } from '../PopOverConfirmationContext';
import { showErrorSnackbar } from 'components/shared/Snackbar';
import { GRAY } from 'constants/colors';
import TaskList, { TASK_LIST_WIDTH } from 'components/shared/TaskList/TaskList';
import DragPortal from '../components/DraggableOverlay/DragPortal';

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5'
      }
    }
  })
};

export default function CardsView({
  startDate,
  planningEntities,
  setPlanningEntities,
  clonedPlanningEntities,
  setClonedPlanningEntities,
  planAppointments,
  connectAppointmentGrouping,
  revertAction,
  isLoading
}: UsePlanningReturn) {
  const [taskListIsOpen, setTaskListIsOpen] = useState<boolean>(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [shouldShowPopoverDialog, setShouldShowPopoverDialog] = useRecoilState(
    shouldShowPopoverDialogAtom
  );

  const [activeEvent, setActiveEvent] = useState<Event | null>(null);

  const [, setDialogState] = useRecoilState(dialogManagerActiveAtom);
  const [, setDragActionActive] = useRecoilState<boolean>(dragActionActiveAtom);
  const [payload, setPayload] = useState<
    PlanAppointmentsRequest | ConnectAppointmentGroupingPayload
  >();

  const recentlyMovedToNewContainer = useRef(false);

  const [activeEntityContainingSelection, setActiveEntityContainingSelection] =
    useState<string | null>(null);

  const [, setPlanResponsePlanningEntityIdentifiers] = useRecoilState(
    planResponsePlanningEntityIdentifiersAtom
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const onMutationError = () => {
    setPayload(undefined);
    revertAction();
    showErrorSnackbar();
    setPendingDropPosition(null);
  };

  const [isLoadingUnassignAppointment, setIsLoadingUnassignAppointment] =
    useState<boolean>(false);

  const { mutateAsync: unassignAppointmentAsync } =
    mutations.useRemoveAssignmentFromAppointments();

  const [pendingDropPosition, setPendingDropPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const handlePlanAppointments = (payload: PlanAppointmentsRequest | null) => {
    handlePlanAppointmentsUtil({
      payload,
      planAppointments,
      setPayload,
      setShouldShowPopoverDialog,
      setDialogState,
      revertAction,
      onMutationError,
      planningEntitiesToEvents,
      getConflictingEvents,
      notificationsToHashTable,
      planningEntities,
      appendCorrelationToken,
      setPlanResponsePlanningEntityIdentifiers,
      onCancelWarningDialog: () => {
        setPendingDropPosition(null);
        setActiveEvent(null);
      },
      onSuccess: () => {
        setSelectedIds([]);
        setActiveEntityContainingSelection(null);
        setPendingDropPosition(null);
        setActiveEvent(null);
      }
    });
  };

  const sendConnectAppointmentGrouping = (
    appointmentGroupingEntityId: string
  ) => {
    if (planningEntities) {
      const payload: ConnectAppointmentGroupingPayload = {
        appointmentGroupingId: appointmentGroupingEntityId,
        appointmentGroupingStart: startDate,
        editAppointmentType: 'ONLY_THIS',
        suppressWarnings: false
      };
      const connectAppointmentPayload = appendCorrelationToken(payload);
      setPayload(connectAppointmentPayload);

      const planningEntity = planningEntities[appointmentGroupingEntityId];
      const today = new Date();
      today.setUTCHours(0, 0, 0, 0);
      if (
        planningEntity.schedules
          .map((s) => s.events)
          .flat()
          .some((event) => event.isRecurring && !event.isAbsent) &&
        new Date(startDate) >= today
      ) {
        setShouldShowPopoverDialog({
          id: appointmentGroupingEntityId,
          scheduleStart: startDate
        });
      } else {
        connectAppointmentGrouping(connectAppointmentPayload, {
          onSuccess: (response: any) => {
            if (!response.data.success) {
              const notifications = response.data
                .notifications as APNotification[];

              const events =
                planningEntities[
                  connectAppointmentPayload.appointmentGroupingId
                ].schedules
                  .map((s) => s.events)
                  .flat() ?? [];
              const conflictingEvents: Event[] = getConflictingEvents(
                events || [],
                notifications
              );

              setDialogState({
                type: DialogType.WARNING,
                open: true,
                data: notificationsToHashTable(
                  conflictingEvents,
                  notifications
                ),
                onSubmit: () => {
                  connectAppointmentGrouping(
                    {
                      ...connectAppointmentPayload,
                      suppressWarnings: true
                    } as ConnectAppointmentGroupingPayload,
                    {
                      onError: onMutationError
                    }
                  );
                },
                onCancel: () => revertAction()
              });
            }
          },
          onError: onMutationError
        });
      }
    }
  };

  function unassignAppointment(editAppointmentType: EditAppointmentType) {
    unassignAppointmentUtil({
      editAppointmentType,
      activeEvent,
      selectedIds,
      planningEntities,
      shouldShowPopoverDialog,
      appendCorrelationToken,
      unassignAppointmentAsync,
      setShouldShowPopoverDialog,
      setPlanResponsePlanningEntityIdentifiers,
      setActiveEntityContainingSelection,
      setIsLoading: setIsLoadingUnassignAppointment,
      onFinally: () => {
        setIsLoadingUnassignAppointment(false);
        setActiveEvent(null);
        setActiveEntityContainingSelection(null);
        setPendingDropPosition(null);
        setSelectedIds([]);
      },
      onGetEvents: (planningEntity, isToPlan, selectedIds) => {
        return (
          (isToPlan
            ? planningEntity?.events.filter(({ id }: { id: string }) =>
                selectedIds.includes(id)
              )
            : planningEntity?.schedules
                .flatMap((s: any) => s.events)
                .filter(({ id }: { id: string }) =>
                  selectedIds.includes(id)
                )) || []
        );
      }
    });
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <PopOverConfirmationContext.Provider
        value={{
          // in ==>
          isLoading,
          // <== out
          onClose: () => {
            setActiveEvent(null);
            setPendingDropPosition(null);
            revertAction();
          },
          onHandleAppointmentConfirmation: (
            editAppointmentType: EditAppointmentType,
            isUnassignment?: boolean
          ) => {
            if (isUnassignment) {
              return unassignAppointment(editAppointmentType);
            }

            if (payload && 'appointmentOccurrences' in payload) {
              handlePlanAppointments({
                ...(payload as PlanAppointmentsRequest),
                editAppointmentType
              });
            } else {
              const connectAppointmentPayload = appendCorrelationToken(
                payload as ConnectAppointmentGroupingPayload
              );
              connectAppointmentGrouping(
                {
                  ...connectAppointmentPayload,
                  editAppointmentType
                } as ConnectAppointmentGroupingPayload,
                {
                  onSuccess: (response: any) => {
                    if (
                      !response.data.success &&
                      connectAppointmentPayload &&
                      'appointmentGroupingId' in connectAppointmentPayload &&
                      planningEntities
                    ) {
                      const events =
                        planningEntities[
                          connectAppointmentPayload.appointmentGroupingId
                        ].schedules
                          .map((s) => s.events)
                          .flat() ?? [];
                      const notifications = response.data
                        .notifications as APNotification[];
                      const conflictingEvents: Event[] = getConflictingEvents(
                        events || [],
                        notifications
                      );

                      setDialogState({
                        type: DialogType.WARNING,
                        open: true,
                        data: notificationsToHashTable(
                          conflictingEvents,
                          notifications
                        ),
                        onSubmit: () => {
                          connectAppointmentGrouping(
                            {
                              ...connectAppointmentPayload,
                              editAppointmentType,
                              suppressWarnings: true
                            } as ConnectAppointmentGroupingPayload,
                            {
                              onError: onMutationError
                            }
                          );
                        },
                        onCancel: () => {
                          revertAction();
                          setActiveEvent(null);
                        }
                      });
                    } else {
                      setActiveEvent(null);
                    }
                  },
                  onError: onMutationError
                }
              );
            }
          }
        }}
      >
        <LoadingOverlay loading={isLoading || isLoadingUnassignAppointment} />
        <Box display="flex" position="relative" overflow="hidden" height="100%">
          <Box
            sx={{
              width: '100%',
              overflowX: 'auto',
              minWidth: taskListIsOpen ? 'initial' : '100%',
              maxWidth: taskListIsOpen
                ? `calc(100% - ${TASK_LIST_WIDTH}px)`
                : 'initial',
              position: 'relative',
              top: 0,
              borderLeft: `1px solid ${GRAY}`
            }}
          >
            <RouteOverview
              activeEntityContainingSelection={activeEntityContainingSelection}
              onChangeSelection={setSelectedIds}
              onChangeActiveEntity={setActiveEntityContainingSelection}
              planningEntities={
                planningEntities ? Object.values(planningEntities) : []
              }
              connectAppointmentGrouping={sendConnectAppointmentGrouping}
            />
          </Box>

          <TaskList
            onOpen={setTaskListIsOpen.bind(undefined, true)}
            onClose={setTaskListIsOpen.bind(undefined, false)}
            planningEntities={planningEntities}
            dropzonePadding="10px"
            activeEntityContainingSelection={activeEntityContainingSelection}
            onChangeActiveEntity={setActiveEntityContainingSelection}
            onChangeSelection={setSelectedIds}
          />
        </Box>
        {activeEvent && (
          <DragPortal
            activeEvent={activeEvent}
            selectedIds={selectedIds}
            pendingDropPosition={pendingDropPosition}
            dropAnimation={dropAnimation}
          />
        )}
      </PopOverConfirmationContext.Provider>
    </DndContext>
  );

  function handleDragCancel() {
    setDragActionActive(false);

    if (clonedPlanningEntities) {
      setPlanningEntities(clonedPlanningEntities);
    }
    setActiveEvent(null);
    setPendingDropPosition(null);
    setClonedPlanningEntities(null);
  }

  function handleDragStart({ active }) {
    setDragActionActive(true);
    recentlyMovedToNewContainer.current = false;

    const activePlanningEntityId = active?.data.current.planningEntityId;
    const isToPlan = active?.data.current.isToPlan;

    const event = !isToPlan
      ? planningEntities?.[activePlanningEntityId].schedules
          .map((s) => s.events)
          .flat()
          .find(({ id }) => id === active.id) ?? null
      : planningEntities?.[activePlanningEntityId].events.find(
          ({ id }) => id === active.id
        ) ?? null;

    setActiveEvent(event);
    setActiveEntityContainingSelection(active.data.current.planningEntityId); // Set active entity for selection (removing selections from any other route)
    setSelectedIds((prev) => (prev.includes(active.id) ? selectedIds : []));
    setClonedPlanningEntities(planningEntities);
  }

  function handleDragEnd({ active, over }) {
    setDragActionActive(false);
    const overId = over?.id;
    const overPlanningEntityId = over?.data.current.planningEntityId;
    const activePlanningEntityId = active?.data.current.planningEntityId;
    const activeIsToPlan = active?.data.current.isToPlan;
    const overIsToPlan = over?.data.current.isToPlan;

    if (!over || activeIsToPlan === overIsToPlan)
      return setPendingDropPosition(null);

    const mouseUpHandler = (e: MouseEvent) => {
      setPendingDropPosition({
        x: e.clientX,
        y: e.clientY
      });

      // Remove the event listener after it has been triggered
      window.removeEventListener('mouseup', mouseUpHandler);
    };

    window.addEventListener('mouseup', mouseUpHandler, { once: true });

    if (active.id === overId) return;

    if (
      activePlanningEntityId === undefined ||
      overId === undefined ||
      !activeEvent ||
      activeIsToPlan === overIsToPlan ||
      (active.id === overId && !recentlyMovedToNewContainer.current)
    ) {
      setActiveEvent(null);
      setPendingDropPosition(null);
      setSelectedIds([]);
      return;
    }

    const ids = selectedIds.length ? selectedIds : [active.id];

    const selectedEvents: Event[] = ids.map(
      (id) => findItem(planningEntities, activePlanningEntityId, id) as Event
    );

    const toPlanPlanningEntity =
      planningEntities &&
      Object.values(planningEntities).find(
        (entity) => entity.type === 'ORGANIZATIONAL_UNIT_TO_PLAN'
      );

    const occurrenceDate = activeEvent.start
      ? format(new Date(activeEvent.start), 'y-MM-dd')
      : startDate;

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    const planningEntityType = overIsToPlan
      ? 'ORGANIZATIONAL_UNIT_TO_PLAN'
      : 'ROUTE';
    const planningEntityId = overIsToPlan
      ? toPlanPlanningEntity?.id
      : overPlanningEntityId;

    const payload: PlanAppointmentsRequest = {
      appointmentOccurrences:
        selectedIds.length === 0
          ? [
              {
                entityId: activeEvent.id,
                originalOccurrenceStart: activeEvent.start,
                schedulingLabel: activeEvent.schedulingLabel
              }
            ]
          : selectedEvents.map((event: Event) => ({
              entityId: event.id,
              originalOccurrenceStart: event.start,
              schedulingLabel: event.schedulingLabel
            })),
      planningEntity: {
        planningEntityType,
        entityId: planningEntityId,
        start:
          !over || overIsToPlan || !planningEntities
            ? undefined
            : over.data.current.scheduleStart
      },
      start: over.data.current.start,
      editAppointmentType: 'ONLY_THIS',
      suppressWarnings: false
    };

    setPayload(payload);

    if (activeEvent?.isRecurring && new Date(occurrenceDate) >= today) {
      setShouldShowPopoverDialog({
        id: activeEvent.id,
        scheduleStart: over.data.current.scheduleStart,
        isUnassignment: overIsToPlan,
        isDragAction: true
      });
    } else if (overIsToPlan) {
      unassignAppointment('ONLY_THIS');
    } else {
      handlePlanAppointments(payload);
    }
  }
}
